(() => {
    const CACHE_KEY = 'nubs:fuzzy-catalog:v10';
    const CACHE_TTL_MS = 2 * 60 * 1000;
    const MAX_PAGES = 80;

    let memoryCatalog = null;
    let memoryCatalogAt = 0;
    let catalogLoad = null;

    const normalize = (value) => String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const asArray = (value) => {
        if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined && String(item).trim() !== '');
        if (value === null || value === undefined || value === '') return [];
        return [value];
    };

    const tokens = (value) => normalize(value).split(' ').filter(Boolean);


    const analyzeQuery = (value) => {
        const original = normalize(value);
        if (!original) return { original: '', effective: '', softMarketing: false };
        let effective = original
            .replace(/\bcomplete\s+(?=(?:seasons?|parts?|sets?|collections?|vol(?:ume)?s?|books?)\s+\d+\b)/g, ' ')
            .replace(/\b(?:the\s+)?complete\s+series\s+collection\b/g, ' ')
            .replace(/\b(?:the\s+)?complete\s+box\s+set\b/g, ' ')
            .replace(/\b(?:the\s+)?complete\s+(?:tv|anime)\s+series\b/g, ' ')
            .replace(/\b(?:the\s+)?complete\s+(?:collection|series|set|edition)\b/g, ' ')
            .replace(/\b(?:the\s+)?(?:full|entire|whole)\s+(?:collection|series|set|box\s+set)\b/g, ' ')
            .replace(/\bcomplete\s*$/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (!effective) effective = original;
        return { original, effective, softMarketing: effective !== original };
    };

    const exactTokenPhrase = (value, query) => {
        const haystack = tokens(value);
        const needleWords = tokens(query);
        if (!haystack.length || !needleWords.length || needleWords.length > haystack.length) return false;
        for (let start = 0; start <= haystack.length - needleWords.length; start += 1) {
            if (needleWords.every((word, index) => haystack[start + index] === word)) return true;
        }
        return false;
    };

    const seasonValues = (value) => String(value || '').split(',').map((item) => Number.parseInt(item.trim(), 10) || 0).filter((item) => item > 0);

    const scopeAliases = (product) => {
        const values = [];
        for (const season of seasonValues(product?.season)) values.push(`season ${season}`);
        const scopeType = String(product?.scope_type || '').trim();
        const scopeNumber = Number.parseInt(product?.scope_number, 10) || 0;
        if (scopeType && scopeNumber > 0) values.push(`${scopeType} ${scopeNumber}`);
        return values;
    };

    const canonicalScopeLabel = (value) => {
        const clean = normalize(value);
        if (['vol', 'vols', 'volume', 'volumes'].includes(clean)) return 'volume';
        if (clean === 'parts') return 'part';
        if (clean === 'sets') return 'set';
        if (clean === 'collections') return 'collection';
        if (clean === 'books') return 'book';
        return clean;
    };

    const structuredNumberRequirements = (query) => {
        const words = tokens(query);
        const scopeWords = new Set(['part', 'parts', 'set', 'sets', 'volume', 'volumes', 'vol', 'vols', 'collection', 'collections', 'book', 'books']);
        const requirements = [];
        for (let index = 0; index < words.length - 1; index += 1) {
            const label = words[index];
            const number = Number.parseInt(words[index + 1], 10);
            if (!Number.isInteger(number) || number <= 0) continue;
            if (label === 'season' || label === 'seasons') requirements.push({ kind: 'season', label: 'season', number });
            else if (scopeWords.has(label)) requirements.push({ kind: 'scope', label: canonicalScopeLabel(label), number });
        }
        return requirements;
    };

    const hasNumberedTitleLabel = (value, label, number) => {
        const words = tokens(value);
        for (let index = 0; index < words.length - 1; index += 1) {
            if (canonicalScopeLabel(words[index]) === canonicalScopeLabel(label) && Number.parseInt(words[index + 1], 10) === number) return true;
        }
        return false;
    };

    const structuredNumberIntentMatches = (product, query) => {
        const requirements = structuredNumberRequirements(query);
        if (!requirements.length) return true;
        const titles = [product?.title, product?.display_title, ...asArray(product?.alternate_titles)].filter(Boolean);
        const seasons = seasonValues(product?.season);
        const scopeType = canonicalScopeLabel(product?.scope_type || '');
        const scopeNumber = Number.parseInt(product?.scope_number, 10) || 0;
        return requirements.every((requirement) => {
            if (requirement.kind === 'season') {
                return seasons.includes(requirement.number) || titles.some((title) => hasNumberedTitleLabel(title, 'season', requirement.number));
            }
            return (scopeType === requirement.label && scopeNumber === requirement.number)
                || titles.some((title) => hasNumberedTitleLabel(title, requirement.label, requirement.number));
        });
    };

    const damerauLevenshtein = (a, b) => {
        const left = String(a || '');
        const right = String(b || '');
        const rows = left.length + 1;
        const cols = right.length + 1;
        const matrix = Array.from({ length: rows }, () => Array(cols).fill(0));

        for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
        for (let j = 0; j < cols; j += 1) matrix[0][j] = j;

        for (let i = 1; i < rows; i += 1) {
            for (let j = 1; j < cols; j += 1) {
                const cost = left[i - 1] === right[j - 1] ? 0 : 1;
                matrix[i][j] = Math.min(
                    matrix[i - 1][j] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j - 1] + cost
                );

                if (
                    i > 1 &&
                    j > 1 &&
                    left[i - 1] === right[j - 2] &&
                    left[i - 2] === right[j - 1]
                ) {
                    matrix[i][j] = Math.min(
                        matrix[i][j],
                        matrix[i - 2][j - 2] + cost
                    );
                }
            }
        }

        return matrix[left.length][right.length];
    };

    const maxUsefulDistance = (length) => {
        if (length <= 3) return 0;
        if (length <= 5) return 1;
        if (length <= 8) return 2;
        if (length <= 12) return 3;
        return 4;
    };

    const parseVariantTerms = (product) => {
        const values = [];
        for (const variant of product.variants || []) {
            if (variant?.sku) values.push(variant.sku);
            if (variant?.barcode) values.push(variant.barcode);
        }
        return values;
    };

    const productIndexText = (product) => [
        product.title,
        product.display_title,
        ...asArray(product.alternate_titles),
        product.included_content,
        ...scopeAliases(product),
        product.vendor,
        product.product_type,
        product.content_category,
        product.universe,
        product.release_type,
        product.edition,
        product.content_rating,
        ...asArray(product.genres),
        ...asArray(product.studios),
        ...asArray(product.regions),
        ...asArray(product.audio),
        ...asArray(product.subtitles),
        ...asArray(product.tags),
        ...parseVariantTerms(product),
    ].filter(Boolean).join(' ');

    const scoreToken = (queryToken, candidateToken) => {
        if (!queryToken || !candidateToken) return 0;
        if (candidateToken === queryToken) return 120;
        if (candidateToken.startsWith(queryToken) || queryToken.startsWith(candidateToken)) return 92;
        if (candidateToken.includes(queryToken) || queryToken.includes(candidateToken)) return 82;

        const distance = damerauLevenshtein(queryToken, candidateToken);
        const allowed = maxUsefulDistance(Math.max(queryToken.length, candidateToken.length));
        if (distance > allowed) return 0;

        return 72 - (distance * 12);
    };

    const scorePhrase = (query, phrase, exact, starts, includes) => {
        const normalizedPhrase = normalize(phrase);
        if (!normalizedPhrase) return 0;
        if (normalizedPhrase === query) return exact;
        if (normalizedPhrase.startsWith(query)) return starts;
        if (normalizedPhrase.includes(query)) return includes;
        return 0;
    };

    const scoreProduct = (query, product) => {
        const intent = analyzeQuery(query);
        const normalizedQuery = intent.effective;
        if (!normalizedQuery) return 0;
        if (!structuredNumberIntentMatches(product, normalizedQuery)) return 0;

        const title = normalize(product.display_title || product.title);
        const alternateTitles = asArray(product.alternate_titles).map(normalize).filter(Boolean);
        const vendor = normalize(product.vendor);
        const type = normalize(product.product_type);
        const searchable = normalize(productIndexText(product));
        const queryTokens = tokens(normalizedQuery);
        const titleTokens = tokens([product.title, product.display_title, ...asArray(product.alternate_titles)].join(' '));
        const allTokens = tokens(searchable);

        let score = 0;

        if (title === normalizedQuery) score += 1100;
        else if (exactTokenPhrase(title, normalizedQuery)) score += title.startsWith(normalizedQuery) ? 980 : 900;
        else score += scorePhrase(normalizedQuery, title, 0, 650, 500);
        for (const alternate of alternateTitles) {
            const alternateScore = alternate === normalizedQuery
                ? 840
                : exactTokenPhrase(alternate, normalizedQuery)
                    ? 780
                    : scorePhrase(normalizedQuery, alternate, 0, 600, 450);
            score = Math.max(score, alternateScore);
        }
        if (vendor === normalizedQuery) score += 330;
        if (type === normalizedQuery) score += 260;
        if (searchable.includes(normalizedQuery)) score += 240;

        let matchedTokens = 0;
        for (const queryToken of queryTokens) {
            let bestTitleScore = 0;
            for (const candidateToken of titleTokens) {
                bestTitleScore = Math.max(bestTitleScore, scoreToken(queryToken, candidateToken));
            }

            let bestAnyScore = bestTitleScore;
            if (bestAnyScore === 0) {
                for (const candidateToken of allTokens) {
                    bestAnyScore = Math.max(bestAnyScore, scoreToken(queryToken, candidateToken));
                }
            }

            if (bestAnyScore > 0) {
                matchedTokens += 1;
                score += bestTitleScore > 0 ? bestTitleScore * 2 : bestAnyScore;
            }
        }

        if (queryTokens.length > 0 && matchedTokens === queryTokens.length) {
            score += 180;
        } else if (matchedTokens === 0) {
            return 0;
        }

        const candidatePhrases = [title, ...alternateTitles].filter(Boolean);
        let bestWholeDistance = Infinity;
        for (const phrase of candidatePhrases) {
            bestWholeDistance = Math.min(bestWholeDistance, damerauLevenshtein(normalizedQuery, phrase));
        }
        if (Number.isFinite(bestWholeDistance) && bestWholeDistance <= maxUsefulDistance(Math.max(normalizedQuery.length, title.length))) {
            score += 260 - (bestWholeDistance * 30);
        }

        if (intent.softMarketing && score > 0) {
            const seasons = seasonValues(product?.season);
            if (seasons.length > 1) score += Math.min(45, 15 + (seasons.length * 10));
            if (/^collection$/i.test(String(product?.scope_type || '').trim())) score += 25;
            if ((Number(product?.episode_count) || 0) >= 20) score += 15;
            if ((Number(product?.ova_count) || 0) > 0) score += 5;
            if ((Number(product?.movie_count) || 0) > 0) score += 5;
        }

        return score;
    };

    const restoreCache = () => {
        try {
            const raw = sessionStorage.getItem(CACHE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed?.createdAt || !Array.isArray(parsed.products)) return null;
            if ((Date.now() - parsed.createdAt) > CACHE_TTL_MS) return null;
            return parsed.products;
        } catch (error) {
            return null;
        }
    };

    const saveCache = (products) => {
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                createdAt: Date.now(),
                products,
            }));
        } catch (error) {
            /* Search should still work when session storage is unavailable. */
        }
    };

    const abortError = () => new DOMException('Search cancelled.', 'AbortError');

    const fetchCatalogPage = async (page, signal) => {
        const url = new URL('/collections/all', window.location.origin);
        url.searchParams.set('view', 'fuzzy-index');
        url.searchParams.set('page', String(page));

        const response = await fetch(url.toString(), {
            headers: { Accept: 'application/json' },
            signal,
        });

        if (!response.ok) {
            throw new Error(`Catalog fallback returned ${response.status}`);
        }

        return response.json();
    };

    const fetchCatalog = async (signal) => {
        if (signal.aborted) throw abortError();
        const cached = restoreCache();
        if (cached) return cached;
        const first = await fetchCatalogPage(1, signal);
        const products = Array.isArray(first?.products) ? [...first.products] : [];
        const totalPages = Math.min(MAX_PAGES, Math.max(1, Math.floor(Number(first?.pages) || 1)));

        // Keep pagination ordered and bounded without one round trip per page.
        for (let start = 2; products.length && start <= totalPages; start += 4) {
            const pageNumbers = [];
            for (let page = start; page < start + 4 && page <= totalPages; page += 1) pageNumbers.push(page);
            const pages = await Promise.all(pageNumbers.map(page => fetchCatalogPage(page, signal)));
            let reachedEnd = false;
            for (const payload of pages) {
                const pageProducts = Array.isArray(payload?.products) ? payload.products : [];
                if (!pageProducts.length) { reachedEnd = true; break; }
                products.push(...pageProducts);
            }
            if (reachedEnd) break;
        }

        if (signal.aborted) throw abortError();
        saveCache(products);
        return products;
    };

    const loadCatalog = async ({signal} = {}) => {
        if (signal?.aborted) throw abortError();
        if (memoryCatalog && Date.now() - memoryCatalogAt < CACHE_TTL_MS) return memoryCatalog;
        if (!catalogLoad || catalogLoad.controller.signal.aborted) {
            const job = {controller:new AbortController(), consumers:0, settled:false, promise:null};
            catalogLoad = job;
            job.promise = fetchCatalog(job.controller.signal)
                .then((products) => {
                    memoryCatalog = products;
                    memoryCatalogAt = Date.now();
                    return products;
                })
                .catch(error => { job.controller.abort(); throw error; })
                .finally(() => {
                    job.settled = true;
                    if (catalogLoad === job) catalogLoad = null;
                });
        }
        const job = catalogLoad;
        // A cancelled dropdown must not abort a full search page using the
        // same download. Stop the fetch only after its last consumer leaves.
        return new Promise((resolve, reject) => {
            job.consumers += 1;
            let finished = false;
            const finish = (handler, value) => {
                if (finished) return;
                finished = true;
                signal?.removeEventListener('abort', onAbort);
                job.consumers -= 1;
                handler(value);
                if (!job.settled && job.consumers === 0) job.controller.abort();
            };
            const onAbort = () => finish(reject, abortError());
            signal?.addEventListener('abort', onAbort, {once:true});
            if (signal?.aborted) onAbort();
            job.promise.then(value => finish(resolve, value), error => finish(reject, error));
        });
    };

    const search = async (query, { limit = 5, signal } = {}) => {
        const normalizedQuery = normalize(query);
        if (normalizedQuery.length < 3) return [];

        const catalog = await loadCatalog({signal});
        if (signal?.aborted) throw abortError();
        const ranked = [];

        for (const product of catalog) {
            const score = scoreProduct(normalizedQuery, product);
            if (score <= 0) continue;
            ranked.push({ product, score });
        }

        ranked.sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return String(a.product.title || '').localeCompare(String(b.product.title || ''), undefined, {
                numeric: true,
                sensitivity: 'base',
            });
        });

        const bestScore = ranked[0]?.score || 0;
        if (bestScore < 120) return [];

        const floor = Math.max(90, bestScore * 0.42);
        return ranked
            .filter((entry) => entry.score >= floor)
            .slice(0, limit)
            .map((entry) => entry.product);
    };

    const isBundle = (product) => (
        product?.is_bundle === true ||
        product?.is_bundle === 'true' ||
        product?.is_bundle === 1 ||
        product?.is_bundle === '1'
    );

    const firstVariant = (product) => window.NubsConditions?.preferred(product) || product?.variants?.[0] || null;

    const fieldValues = (product, field) => {
        switch (field) {
            case 'availability': return [product?.available ? 'In stock' : 'Out of stock'];
            case 'format': return asArray(product?.product_type);
            case 'publisher': return asArray(product?.vendor);
            case 'category': return asArray(product?.content_category);
            case 'genres': return asArray(product?.genres);
            case 'studios': return asArray(product?.studios);
            case 'universe': return asArray(product?.universe);
            case 'content_type': return asArray(product?.release_type);
            case 'region': return asArray(product?.regions);
            case 'audio': return asArray(product?.audio);
            case 'subtitles': return asArray(product?.subtitles);
            case 'edition': return asArray(product?.edition);
            case 'content_rating': return asArray(product?.content_rating);
            case 'release_year': return asArray(product?.release_year);
            default: return [];
        }
    };

    window.NubsFuzzySearch = {
        asArray,
        fieldValues,
        firstVariant,
        isBundle,
        loadCatalog,
        normalize,
        search,
    };
})();
